<template>
  <div class="login-container">
    <div class="left-section">
      <video
        class="hero-video"
        :key="heroSrc"
        :src="heroSrc"
        :poster="heroPosterSrc"
        autoplay
        muted
        loop
        playsinline
      ></video>
      <div class="logo">
        <img :src="logoWhite" alt="Peelo Logo" />
      </div>
      <a href="https://peelo.io" class="back-link">
        <span>{{ $t('common.backToSite') }}</span>
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M5 12h14M12 5l7 7-7 7"/>
        </svg>
      </a>
      <div class="hero-content">
        <TransitionGroup name="fade-slide">
          <div v-for="(slide, index) in slides" :key="'slide-' + index" v-show="currentSlide === index" class="slide">
            <h2>{{ slide.title }}</h2>
            <p>{{ slide.description }}</p>
          </div>
        </TransitionGroup>
        <div class="dots">
          <span
            v-for="(slide, index) in slides"
            :key="'dot-' + index"
            :class="['dot', { active: currentSlideIndex === index }]"
            @click="setSlide(index)"
          ></span>
        </div>
      </div>
    </div>

    <div class="right-section">
      <div class="auth-container">
        <template v-if="!registrationSuccess">
          <div class="back-button-container">
            <router-link to="/login" class="back-button">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
              <span>{{ $t('common.back') }}</span>
            </router-link>
          </div>

          <h1>{{ $t('register.title') }}</h1>
          <p class="login-text">
            {{ $t('auth.alreadyRegistered') }}
            <router-link to="/login" class="login-link">{{ $t('auth.loginButton') }}</router-link>
          </p>

          <!-- Raccourci Google : inscription sans aucun formulaire -->
          <div class="google-auth-section">
            <GoogleLogin :callback="handleGoogleCallback">
              <button type="button" class="submit-btn google-login-btn">
                <svg class="google-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 48 48">
                  <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"></path>
                  <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"></path>
                  <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.222,0-9.653-3.343-11.303-8l-6.571,4.819C9.656,39.663,16.318,44,24,44z"></path>
                  <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"></path>
                </svg>
                <span>{{ $t('auth.googleRegister') }}</span>
              </button>
            </GoogleLogin>
          </div>

          <div class="separator">
            <span>{{ $t('auth.orRegisterWithEmail') }}</span>
          </div>

          <!-- Formulaire ESSENTIEL uniquement. Le reste (entreprise, pays, téléphone,
               objectif) est demandé après la connexion, sur /onboarding. -->
          <form @submit.prevent="registerQuick" class="register-form">
            <div class="name-row">
              <div class="form-group">
                <label for="firstname">{{ $t('register.firstname') }}</label>
                <input
                  type="text" id="firstname" v-model="formData.firstname"
                  :placeholder="$t('register.firstnamePlaceholder')"
                  :class="{ 'input-error': fieldErrors.firstname }"
                />
              </div>
              <div class="form-group">
                <label for="lastname">{{ $t('register.lastname') }}</label>
                <input
                  type="text" id="lastname" v-model="formData.lastname"
                  :placeholder="$t('register.lastnamePlaceholder')"
                  :class="{ 'input-error': fieldErrors.lastname }"
                />
              </div>
            </div>

            <div class="form-group">
              <label for="email">{{ $t('auth.email') }}</label>
              <input
                type="email" id="email" v-model="formData.email"
                :placeholder="$t('auth.emailPlaceholder')"
                :class="{ 'input-error': fieldErrors.email }"
              />
              <div v-if="fieldErrors.email" class="error-message">{{ fieldErrors.email }}</div>
            </div>

            <div class="form-group">
              <label for="password">{{ $t('auth.password') }}</label>
              <div class="password-input-wrapper">
                <input
                  :type="showPassword ? 'text' : 'password'" id="password" v-model="formData.password"
                  :placeholder="$t('register.passwordChoose')"
                  :class="{ 'input-error': fieldErrors.password }"
                />
                <span class="password-toggle" @click="showPassword = !showPassword">
                  <svg v-if="!showPassword" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                </span>
              </div>
              <small class="form-text">{{ $t('register.passwordHint') }}</small>
              <div v-if="fieldErrors.password" class="error-message">{{ fieldErrors.password }}</div>
            </div>

            <!-- Vérification humaine (compacte) -->
            <div class="form-group">
              <div class="captcha-row" :class="{ verified: isHuman }">
                <canvas ref="captchaCanvas" width="132" height="46"></canvas>
                <button type="button" class="refresh-captcha" @click="generateCaptcha" :title="$t('register.securityCheck')">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.3"/>
                  </svg>
                </button>
                <input
                  type="text" v-model="captchaInput"
                  :placeholder="$t('register.captchaPlaceholder')"
                  class="captcha-input" @input="checkCaptcha"
                />
                <svg v-if="isHuman" class="captcha-ok" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#25D366" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              </div>
            </div>

            <label class="checkbox-container">
              <input type="checkbox" v-model="formData.termsAccepted" :class="{ 'input-error': fieldErrors.terms }" />
              <span class="checkbox-text">
                {{ $t('register.acceptTerms') }} <a href="https://peelo.io/cgu" target="_blank">{{ $t('register.termsOfService') }}</a>
              </span>
            </label>
            <div v-if="fieldErrors.terms" class="error-message">{{ fieldErrors.terms }}</div>

            <div v-if="fieldErrors.general" class="general-error-message">{{ fieldErrors.general }}</div>

            <button type="submit" class="submit-btn full-width" :disabled="isLoading">
              <span v-if="isLoading" class="loader"></span>
              <span v-else>{{ $t('register.registerButton') }}</span>
            </button>
          </form>

          <p class="terms">
            {{ $t('register.privacyNote') }}
            <a href="https://peelo.io/policy" target="_blank">{{ $t('register.privacyPolicy') }}</a>
          </p>
        </template>

        <!-- Succès -->
        <div v-else class="success-container">
          <div class="success-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#25D366" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
          </div>
          <h3 class="success-title">{{ $t('register.successTitle') }}</h3>
          <p class="success-text">{{ $t('register.successRedirect') }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useRouter } from "vue-router";
import { ref, onMounted, onBeforeUnmount, nextTick, computed } from 'vue';
import logoWhite from '@/assets/logo-white.png';
import heroVideo from '@/assets/hero-3d.mp4';
import heroVideoMobile from '@/assets/hero-3d-mobile.mp4';
import heroPoster from '@/assets/hero-3d-poster.webp';
import heroPosterMobile from '@/assets/hero-3d-poster-mobile.webp';
import { useI18n } from 'vue-i18n';
import axios from 'axios';
import { apiBaseUrl } from '@/config';
import { setSsoCookie } from '@/utils/ssoCookie';

export default {
  name: 'UserRegister',
  setup() {
    const { t } = useI18n();
    const router = useRouter();

    // --- Carrousel hero ---
    const currentSlideIndex = ref(0);
    const currentSlide = ref(0);
    const isTransitioning = ref(false);

    // --- Vidéo hero : 9:16 desktop, 16:9 mobile (même breakpoint que le CSS) ---
    const heroMediaQuery = window.matchMedia('(max-width: 768px)');
    const isMobileHero = ref(heroMediaQuery.matches);
    const onHeroMediaChange = (e) => { isMobileHero.value = e.matches; };
    const heroSrc = computed(() => (isMobileHero.value ? heroVideoMobile : heroVideo));
    const heroPosterSrc = computed(() => (isMobileHero.value ? heroPosterMobile : heroPoster));

    // --- État du formulaire ESSENTIEL ---
    const isLoading = ref(false);
    const showPassword = ref(false);
    const registrationSuccess = ref(false);
    const formData = ref({ firstname: '', lastname: '', email: '', password: '', termsAccepted: false });
    const fieldErrors = ref({ firstname: '', lastname: '', email: '', password: '', terms: '', general: '' });

    // --- Captcha ---
    const isHuman = ref(false);
    const captchaResponse = ref('');
    const captchaInput = ref('');
    const captchaText = ref('');
    const captchaCanvas = ref(null);

    const slides = computed(() => [
      { title: t('slides.slide1.title'), description: t('slides.slide1.description') },
      { title: t('slides.slide2.title'), description: t('slides.slide2.description') },
      { title: t('slides.slide3.title'), description: t('slides.slide3.description') }
    ]);

    let slideInterval;
    const setSlide = (index) => {
      if (isTransitioning.value) return;
      isTransitioning.value = true;
      currentSlideIndex.value = index;
      currentSlide.value = index;
      setTimeout(() => { isTransitioning.value = false; }, 800);
    };
    const startSlideShow = () => {
      slideInterval = setInterval(() => {
        setSlide((currentSlideIndex.value + 1) % slides.value.length);
      }, 7000);
    };

    // --- Captcha canvas ---
    const generateCaptcha = () => {
      const canvas = captchaCanvas.value;
      if (!canvas) return;
      try {
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const chars = '0123456789ABCDEFGHJKLMNPQRSTUVWXYZ';
        let captcha = '';
        for (let i = 0; i < 5; i++) captcha += chars.charAt(Math.floor(Math.random() * chars.length));
        captchaText.value = captcha;
        captchaInput.value = '';
        isHuman.value = false;
        captchaResponse.value = '';

        ctx.fillStyle = '#0A1A16';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        for (let i = 0; i < 40; i++) {
          ctx.fillStyle = `rgba(218,255,209,${Math.random() * 0.4})`;
          ctx.fillRect(Math.random() * canvas.width, Math.random() * canvas.height, 2, 2);
        }
        for (let i = 0; i < 3; i++) {
          ctx.strokeStyle = `rgba(218,255,209,${0.25 + Math.random() * 0.3})`;
          ctx.beginPath();
          ctx.moveTo(Math.random() * canvas.width, Math.random() * canvas.height);
          ctx.lineTo(Math.random() * canvas.width, Math.random() * canvas.height);
          ctx.stroke();
        }
        ctx.font = 'bold 22px Arial';
        ctx.fillStyle = '#DAFFD1';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const textWidth = ctx.measureText(captcha).width;
        const spacing = (canvas.width - textWidth) / (captcha.length + 1);
        for (let i = 0; i < captcha.length; i++) {
          const x = (i + 1) * spacing + textWidth * (i / captcha.length);
          const y = canvas.height / 2 + Math.random() * 6 - 3;
          ctx.save();
          ctx.translate(x, y);
          ctx.rotate((Math.random() - 0.5) * 0.35);
          ctx.fillText(captcha[i], 0, 0);
          ctx.restore();
        }
      } catch (_) { /* ignore */ }
    };
    const checkCaptcha = () => {
      if (captchaInput.value.toLowerCase() === captchaText.value.toLowerCase() && captchaText.value) {
        isHuman.value = true;
        captchaResponse.value = 'valid-captcha-response';
      } else {
        isHuman.value = false;
        captchaResponse.value = '';
      }
    };

    const resetFieldErrors = () => {
      Object.keys(fieldErrors.value).forEach((k) => { fieldErrors.value[k] = ''; });
    };

    // --- Inscription rapide (essentiel) -> /onboarding ---
    const registerQuick = async () => {
      resetFieldErrors();
      let hasErr = false;
      if (!formData.value.firstname.trim()) { fieldErrors.value.firstname = 'Le prénom est requis'; hasErr = true; }
      if (!formData.value.lastname.trim()) { fieldErrors.value.lastname = 'Le nom est requis'; hasErr = true; }
      if (!formData.value.email.trim()) {
        fieldErrors.value.email = "L'email est requis"; hasErr = true;
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.value.email)) {
        fieldErrors.value.email = 'Adresse email invalide'; hasErr = true;
      }
      if (!formData.value.password || formData.value.password.length < 8) {
        fieldErrors.value.password = 'Au moins 8 caractères'; hasErr = true;
      }
      if (!formData.value.termsAccepted) {
        fieldErrors.value.terms = "Vous devez accepter les conditions d'utilisation"; hasErr = true;
      }
      if (!isHuman.value || captchaResponse.value !== 'valid-captcha-response') {
        fieldErrors.value.general = 'Veuillez résoudre le captcha'; hasErr = true;
      }
      if (hasErr) return;

      try {
        isLoading.value = true;
        const res = await axios.post(`${apiBaseUrl}/strong/register/quick`, {
          firstname: formData.value.firstname.trim(),
          lastname: formData.value.lastname.trim(),
          email: formData.value.email.trim(),
          password: formData.value.password,
          captchaResponse: captchaResponse.value
        });

        if (res.data && res.data.token) {
          const userData = res.data.data;
          userData.token = res.data.token;
          localStorage.setItem('user', JSON.stringify({ data: userData }));
          setSsoCookie(res.data.token);
          registrationSuccess.value = true;
          // Le compte est registration_incomplete -> on va finaliser le profil.
          setTimeout(() => { router.push('/onboarding'); }, 1200);
        }
      } catch (error) {
        const data = error.response && error.response.data;
        if (data && data.field === 'email') {
          fieldErrors.value.email = data.error;
        } else if (data && data.field === 'password') {
          fieldErrors.value.password = data.error;
        } else {
          generateCaptcha();
          fieldErrors.value.general = (data && data.error) || "Échec de l'inscription. Veuillez réessayer.";
        }
      } finally {
        isLoading.value = false;
      }
    };

    // --- Inscription/Connexion Google (raccourci) ---
    const finishGoogleAuth = async (res) => {
      if (res.data && res.data.token) {
        const userData = res.data.data;
        userData.token = res.data.token;
        localStorage.setItem('user', JSON.stringify({ data: userData }));
        setSsoCookie(res.data.token);
        if (userData.registration_incomplete) {
          await router.push('/onboarding');
        } else if (userData.userType === 'peeloshop') {
          window.location.href = 'https://admin.peelo.shop';
        } else {
          await router.push('/dashboard');
        }
      }
    };
    const handleGoogleCallback = async (response) => {
      try {
        isLoading.value = true;
        fieldErrors.value.general = '';
        if (response.access_token) {
          const info = await axios.get('https://www.googleapis.com/oauth2/v3/userinfo', {
            headers: { Authorization: `Bearer ${response.access_token}` }
          });
          const res = await axios.post(`${apiBaseUrl}/secure/auth/google/userinfo`, {
            email: info.data.email, name: info.data.name, picture: info.data.picture, googleId: info.data.sub
          });
          await finishGoogleAuth(res);
        } else if (response.credential) {
          const res = await axios.post(`${apiBaseUrl}/secure/auth/google`, { token: response.credential });
          await finishGoogleAuth(res);
        } else {
          fieldErrors.value.general = 'Réponse Google invalide. Veuillez réessayer.';
        }
      } catch (_) {
        fieldErrors.value.general = "Erreur lors de l'inscription avec Google. Veuillez réessayer.";
      } finally {
        isLoading.value = false;
      }
    };

    onMounted(() => {
      heroMediaQuery.addEventListener('change', onHeroMediaChange);
      startSlideShow();
      nextTick(() => generateCaptcha());
    });
    onBeforeUnmount(() => {
      heroMediaQuery.removeEventListener('change', onHeroMediaChange);
      clearInterval(slideInterval);
    });

    return {
      currentSlideIndex, currentSlide, slides, setSlide,
      heroSrc, heroPosterSrc,
      logoWhite,
      formData, fieldErrors,
      isLoading, showPassword, registrationSuccess,
      isHuman, captchaInput, captchaCanvas, generateCaptcha, checkCaptcha,
      registerQuick, handleGoogleCallback
    };
  }
};
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  display: grid;
  grid-template-columns: 1fr 1fr;
  background: linear-gradient(160deg, var(--dark-bg) 0%, var(--dark-bg-deep) 100%);
  overflow: hidden;
}

/* ---- Panneau gauche (vidéo hero) ---- */
.left-section {
  position: relative;
  overflow: hidden;
  border-radius: 24px;
  margin: 1rem;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 2rem);
  max-height: calc(100vh - 2rem);
}

/* Sélecteur doublé pour battre la règle `.left-section > *` (position/z-index) */
.left-section .hero-video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.8s ease;
  z-index: 0;
}
.left-section:hover .hero-video { transform: scale(1.08); }

.left-section::after {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: linear-gradient(to bottom, rgba(37, 211, 102, 0.6), rgba(37, 211, 102, 0.2));
  z-index: 1;
}
.left-section > * { position: relative; z-index: 2; }

.logo { height: 48px; }
.logo img { height: 100%; object-fit: contain; }

.back-link {
  position: absolute;
  top: 2rem; right: 2rem;
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(18, 140, 126, 0.2);
  border-radius: 20px;
  font-size: 14px;
  transition: background-color 0.2s;
}
.back-link:hover { background: rgba(18, 140, 126, 0.3); }

.hero-content {
  margin-top: auto;
  margin-bottom: 3rem;
  color: white;
  position: relative;
  height: auto;
  min-height: 220px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  overflow: visible;
}
.slide { width: 100%; position: absolute; top: 0; left: 0; right: 0; bottom: 0; overflow: visible; }
.slide h2 { font-size: 2.5rem; font-weight: 600; margin-bottom: 1rem; }
.slide p { font-size: 1.2rem; line-height: 1.5; opacity: 0.9; margin-bottom: 2rem; }

.dots { display: flex; gap: 0.8rem; margin-top: 140px; justify-content: center; }
.dot { width: 2.5rem; height: 4px; background: rgba(255, 255, 255, 0.2); border-radius: 2px; cursor: pointer; transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1); }
.dot:hover { background: rgba(255, 255, 255, 0.4); }
.dot.active { background: white; width: 4rem; }

.fade-slide-enter-active { transition: all 0.8s ease-out; transition-delay: 0.2s; }
.fade-slide-leave-active { transition: all 0.6s ease-in; position: absolute; }
.fade-slide-enter-from { opacity: 0; transform: translateY(30px); }
.fade-slide-leave-to { opacity: 0; transform: translateY(-30px); }

/* ---- Panneau droit (formulaire) ---- */
.right-section {
  padding: 2rem 4rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow-y: auto;
  overflow-x: hidden;
}
.auth-container { max-width: 420px; width: 100%; margin: 0 auto; }

.back-button-container { margin-bottom: 1.25rem; }
.back-button {
  display: inline-flex; align-items: center; gap: 0.5rem;
  color: rgba(255, 255, 255, 0.7); text-decoration: none; font-size: 0.95rem;
  transition: all 0.2s ease; padding: 0.5rem 0;
}
.back-button:hover { color: white; transform: translateX(-5px); }

h1 { color: white; font-size: 2.3rem; font-weight: 500; margin-bottom: 0.5rem; }
.login-text { color: #8f8f8f; margin-bottom: 1.75rem; }
.login-link { color: var(--dark-accent); text-decoration: none; font-weight: 500; }

.google-auth-section { width: 100%; }
.google-auth-section > div { width: 100%; }
.google-login-btn {
  background: white !important;
  color: var(--dark-bg) !important;
  display: flex; align-items: center; justify-content: center; gap: 12px;
  border: 1px solid rgba(0,0,0,0.1) !important;
  margin-top: 0;
}
.google-login-btn:hover { background: #f8f9fa !important; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
.google-icon { width: 20px; height: 20px; }

.separator { display: flex; align-items: center; text-align: center; margin: 1.5rem 0; color: #8f8f8f; }
.separator::before, .separator::after { content: ''; flex: 1; border-bottom: 1px solid rgba(255, 255, 255, 0.1); }
.separator span { padding: 0 10px; font-size: 0.9rem; }

.name-row { display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; }

.form-group { margin-bottom: 1.1rem; }
.form-group label { display: block; color: white; margin-bottom: 0.5rem; font-size: 0.875rem; }
.form-group input[type="text"],
.form-group input[type="email"],
.form-group input[type="password"] {
  width: 100%; box-sizing: border-box; padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 8px;
  background: transparent; color: white; font-size: 1rem;
}
.form-group input:focus { outline: none; border-color: rgba(255, 255, 255, 0.3); }
.form-text { display: block; margin-top: 0.35rem; color: rgba(255,255,255,0.45); font-size: 0.78rem; }

.password-input-wrapper { position: relative; display: flex; align-items: center; }
.password-input-wrapper input { padding-right: 2.5rem; }
.password-toggle { position: absolute; right: 12px; cursor: pointer; color: rgba(255,255,255,0.6); display: flex; align-items: center; z-index: 10; }
.password-toggle:hover { color: white; }

/* Captcha compact */
.captcha-row {
  display: flex; align-items: center; gap: 8px;
  border: 1px solid rgba(255,255,255,0.1); border-radius: 8px; padding: 6px 8px;
}
.captcha-row.verified { border-color: rgba(37, 211, 102, 0.5); }
.captcha-row canvas { border-radius: 5px; display: block; flex-shrink: 0; }
.refresh-captcha {
  background: rgba(255,255,255,0.06); border: none; border-radius: 6px;
  width: 32px; height: 32px; display: flex; align-items: center; justify-content: center;
  color: rgba(255,255,255,0.7); cursor: pointer; flex-shrink: 0; transition: all 0.2s;
}
.refresh-captcha:hover { background: rgba(255,255,255,0.12); color: white; }
.captcha-input { flex: 1; min-width: 0; background: transparent; border: none; color: white; font-size: 0.95rem; letter-spacing: 2px; }
.captcha-input:focus { outline: none; }
.captcha-ok { flex-shrink: 0; }

.checkbox-container { display: flex; align-items: flex-start; gap: 0.6rem; cursor: pointer; margin: 0.25rem 0 0.75rem; }
.checkbox-container input { margin-top: 3px; width: 16px; height: 16px; accent-color: var(--dark-accent); flex-shrink: 0; }
.checkbox-text { color: rgba(255,255,255,0.75); font-size: 0.85rem; line-height: 1.4; }
.checkbox-text a { color: var(--dark-accent); text-decoration: none; }
.checkbox-text a:hover { text-decoration: underline; }

.submit-btn {
  width: 100%; padding: 0.8rem; border: none; border-radius: 8px;
  background: white; color: var(--dark-bg); font-size: 1rem; font-weight: 600;
  cursor: pointer; transition: all 0.2s; display: flex; align-items: center; justify-content: center;
}
.submit-btn:hover { background: rgba(255, 255, 255, 0.9); transform: translateY(-1px); }
.submit-btn:disabled { background: rgba(255, 255, 255, 0.5); cursor: not-allowed; }
.full-width { margin-top: 0.5rem; }

.loader { display: inline-block; width: 20px; height: 20px; border: 2px solid rgba(20,48,40,0.3); border-radius: 50%; border-top-color: var(--dark-bg); animation: spin 1s ease-in-out infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

.input-error { border-color: #ff5252 !important; }
.error-message { color: #ff5252; font-size: 0.8rem; margin-top: 0.35rem; }
.general-error-message {
  color: #ff5252; font-size: 0.875rem; margin: 0.5rem 0 1rem; padding: 0.75rem;
  background-color: rgba(255, 82, 82, 0.1); border-radius: 8px; border-left: 3px solid #ff5252;
}

.terms { margin-top: 1.5rem; font-size: 0.8rem; color: #8f8f8f; text-align: center; }
.terms a { color: rgba(255,255,255,0.8); text-decoration: none; font-weight: 500; }
.terms a:hover { text-decoration: underline; }

.success-container { display: flex; flex-direction: column; align-items: center; text-align: center; padding: 3rem 0; animation: fadeIn 0.5s ease; }
.success-icon { margin-bottom: 1.5rem; }
.success-title { color: #25D366; font-size: 1.5rem; margin-bottom: 0.75rem; }
.success-text { color: #8f8f8f; line-height: 1.5; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(-5px); } to { opacity: 1; transform: translateY(0); } }

@media (max-width: 768px) {
  .login-container { grid-template-columns: 1fr; overflow-y: auto; }

  /* Le formulaire d'abord, en flux naturel (confortable au doigt sur mobile) */
  .right-section { order: 1; padding: 1.5rem 1.25rem 2rem; min-height: auto; }
  .auth-container { margin: 0 auto; padding: 0; }

  /* La vidéo devient une bannière compacte sous le formulaire */
  .left-section { order: 2; min-height: 220px; max-height: 260px; margin: 0.75rem; padding: 1.25rem; }
  .logo img { height: 32px; }
  .back-link { top: 1rem; right: 1rem; padding: 0.35rem 0.75rem; font-size: 13px; }
  .hero-content { margin-bottom: 0.5rem; min-height: 110px; }

  h1 { font-size: 2rem; }
  .slide { padding: 0; }
  .slide h2 { font-size: 1.4rem; }
  .slide p { font-size: 0.9rem; }
  .dots { padding: 0; margin-top: 1.5rem; }
}
</style>
